// src/cockpit4/components/IPPersonaManager.tsx
// ============================================
// IP 人设管理系统（媒体工作室 P2-功能9）- v2.0 详细改进计划
// AI 人设定义 / 人设一致性检测 / 人设进化 / 多 IP 协同 / 人设知识库
// 数据来源：localStorage（xeaos_ip_persona）
// ============================================

import { useState } from "react";
import { motion } from "framer-motion";
import { UserCircle, Sparkles, ShieldCheck, RefreshCw, BookOpen, Plus } from "lucide-react";

interface Persona {
  id: string;
  name: string;
  avatar: string;
  tone: string;
  style: string;
  slogan: string;
  values: string;
  story: string;
  consistency: number;
  contentCount: number;
}

const INITIAL_PERSONAS: Persona[] = [
  { id: "p1", name: "小舍 AI", avatar: "🤖", tone: "专业 + 亲和", style: "数据驱动 / 清晰 / 有洞察", slogan: "让每个企业都拥有 AI 经营团队", values: "真实 / 务实 / 长期主义", story: "从 0 到 1 的 AI 创业实践者，坚持用 Obsidian 作为唯一大脑", consistency: 94, contentCount: 320 },
  { id: "p2", name: "夏老师", avatar: "👩‍🏫", tone: "干货 + 激励", style: "步骤化教学 / 案例丰富", slogan: "把复杂的事讲简单", values: "利他 / 分享 / 持续成长", story: "10 年企业数字化培训讲师，擅长把方法论拆解成可执行步骤", consistency: 88, contentCount: 180 },
  { id: "p3", name: "老周", avatar: "🧑‍💼", tone: "犀利 + 实战", style: "直击痛点 / 商学院风格", slogan: "不做无效努力", values: "效率 / 结果 / 复盘", story: "连续创业者，专注中小企业经营管理效率提升", consistency: 82, contentCount: 96 },
];

const KEY = "xeaos_ip_persona";

function load(): Persona[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as Persona[];
  } catch { /* ignore */ }
  return INITIAL_PERSONAS;
}

function checkConsistency(text: string, persona: Persona): { score: number; issues: string[] } {
  if (!text.trim()) return { score: 0, issues: [] };
  let score = 70;
  const issues: string[] = [];
  if (persona.style.includes("数据")) { if (!/数字|\d|%/.test(text)) { score -= 12; issues.push("缺少数据支撑，不符合「数据驱动」人设"); } }
  if (persona.tone.includes("亲和") || persona.tone.includes("激励")) { if (!/[！!～~]/.test(text)) { score -= 8; issues.push("语气偏冷，建议加入亲和表达"); } }
  if (/绝对|一定|保证|最厉害/.test(text)) { score -= 10; issues.push("含绝对化用词，与人设「务实」冲突"); }
  if (text.length < 50) { score -= 10; issues.push("内容过短，无法充分体现人设价值主张"); }
  return { score: Math.max(0, Math.min(100, score)), issues };
}

export default function IPPersonaManager() {
  const [personas] = useState<Persona[]>(load);
  const [selectedId, setSelectedId] = useState<string>("p1");
  const [draft, setDraft] = useState("");

  const selected = personas.find((p) => p.id === selectedId) ?? personas[0];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <UserCircle size={14} className="text-pink-400" />
        <h3 className="text-[12px] font-medium text-white">IP 人设管理系统</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能9 · IP Persona</span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {personas.map((p) => {
          const isSel = p.id === selectedId;
          return (
            <button
              key={p.id}
              onClick={() => setSelectedId(p.id)}
              className={`rounded-lg border p-3 text-left transition-colors ${isSel ? "border-pink-500/40 bg-pink-500/10" : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"}`}
            >
              <div className="flex items-center gap-2">
                <span className="text-xl">{p.avatar}</span>
                <div>
                  <div className="text-[11px] font-medium text-white">{p.name}</div>
                  <div className="text-[9px] font-mono text-gray-500">{p.tone} · {p.style}</div>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <motion.div className="h-full rounded-full" initial={{ width: 0 }} animate={{ width: `${p.consistency}%` }} transition={{ duration: 0.5 }} style={{ background: p.consistency >= 85 ? "#34D399" : "#FBBF24" }} />
                </div>
                <span className="text-[9px] font-mono text-gray-400">{p.consistency}%</span>
              </div>
              <div className="text-[9px] font-mono text-gray-600 mt-1.5">{p.contentCount} 篇内容 · 人设一致性</div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen size={11} className="text-sky-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">人设档案 · {selected.name}</span>
          </div>
          <div className="space-y-1.5 text-[9px] font-mono">
            <Row label="语气" value={selected.tone} />
            <Row label="风格" value={selected.style} />
            <Row label="口头禅" value={selected.slogan} />
            <Row label="价值观" value={selected.values} />
            <Row label="背景故事" value={selected.story} />
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-[9px] font-mono text-emerald-400">
            <ShieldCheck size={10} /> 人设知识库已同步至 Vault（Persona/{selected.name}.md）
          </div>
        </div>

        <div className="col-span-7 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={11} className="text-amber-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">人设一致性检测</span>
          </div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={`输入待检测内容，检查是否符合「${selected.name}」的人设...`}
            className="w-full h-24 rounded-md border border-white/[0.06] bg-black/20 px-2.5 py-2 text-[10px] font-mono text-gray-300 placeholder-gray-600 outline-none focus:border-pink-500/40 resize-none"
          />
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => setDraft("我们开发了一套系统，帮助企业提升经营效率 30%，具体数据：平均节省 12 小时/周，已服务 20+ 中小企业！")}
              className="flex items-center gap-1 px-2 py-1 rounded-md border border-white/[0.06] bg-white/[0.02] text-[9px] font-mono text-gray-400 hover:text-white"
            >
              <Plus size={9} /> 合规示例
            </button>
            <button
              onClick={() => setDraft("我们绝对是最厉害的，保证效果最好，全网第一！")}
              className="flex items-center gap-1 px-2 py-1 rounded-md border border-white/[0.06] bg-white/[0.02] text-[9px] font-mono text-gray-400 hover:text-white"
            >
              <RefreshCw size={9} /> 反例
            </button>
          </div>
          {draft.trim() && (() => {
            const result = checkConsistency(draft, selected);
            return (
              <div className="mt-2 rounded-md border border-white/[0.05] bg-white/[0.01] p-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono">一致性得分</span>
                  <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${result.score}%`, background: result.score >= 80 ? "#34D399" : result.score >= 60 ? "#FBBF24" : "#F87171" }} />
                  </div>
                  <span className="text-[10px] font-mono font-semibold" style={{ color: result.score >= 80 ? "#34D399" : result.score >= 60 ? "#FBBF24" : "#F87171" }}>{result.score}</span>
                </div>
                {result.issues.map((issue, i) => (
                  <div key={i} className="flex items-start gap-1.5 mt-1.5 text-[9px] font-mono text-amber-400">⚠ {issue}</div>
                ))}
                {result.issues.length === 0 && <div className="text-[9px] font-mono text-emerald-400 mt-1.5">✓ 内容符合「{selected.name}」人设定位</div>}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-white/[0.04] bg-white/[0.01] px-2.5 py-1.5">
      <span className="text-gray-500 w-14 flex-shrink-0">{label}</span>
      <span className="text-gray-300 leading-relaxed">{value}</span>
    </div>
  );
}

